import React, { useState } from 'react';
import { Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useGetStoreExpensesQuery,
  useBulkUpdateStoreExpensesMutation,
  useUploadStoreExpenseImagesMutation,
} from '../../slices/storeExpensesApiSlice';

const BulkStoreTransferBatchUpdateScreen = () => {
  const [updates, setUpdates] = useState([]);
  const [fileName, setFileName] = useState('');
  const [results, setResults] = useState([]);

  const { data } = useGetStoreExpensesQuery({ keyword: '', pageNumber: 1 });
  const [bulkUpdateStoreExpenses, { isLoading: loadingUpdate }] = useBulkUpdateStoreExpensesMutation();
  const [uploadStoreExpenseImages, { isLoading: loadingUpload }] = useUploadStoreExpenseImagesMutation();

  const isValidObjectId = (id) => /^[a-f\d]{24}$/i.test(id);
  const formatDate = (value) => {
    const parsed = new Date(value);
    return !isNaN(parsed) ? parsed.toISOString().split('T')[0] : '';
  };

  const validateRow = (row) => {
    if (!row.StoreExpenseId || !isValidObjectId(row.StoreExpenseId.trim())) return 'Invalid or missing StoreExpense ID';
    if (!row.AccountName) return 'Missing Account Name';
    if (!['Debit', 'Credit'].includes(row.Account)) return 'Invalid Account';
    if (isNaN(parseFloat(row.Amount))) return 'Invalid Amount';
    if (!row.Date || isNaN(Date.parse(row.Date))) return 'Invalid Date';
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !data?.storeExpenses) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = rows.map((row, index) => {
        const storeExpenseId = row.StoreExpenseId.trim();
        const match = data.storeExpenses.find((t) => t._id === storeExpenseId);
        const error = validateRow(row) || (!match ? 'No matching storeExpense found' : null);

        return {
          index,
          storeExpenseId,
          matchedStoreExpense: match,
          date: formatDate(row.Date),
          account: row.Account,
          accountName: row.AccountName,
          subAccount: row.SubAccount,
          details: row.Details,
          amount: parseFloat(row.Amount),
          category: row.Category,
          notes: row.Notes,
          images: row.Images ? row.Images.split(',').map((url) => url.trim()) : [],
          error,
        };
      });

      setUpdates(enriched);
      setResults([]);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('images', file);

    try {
      const res = await uploadStoreExpenseImages(formData).unwrap();
      const updated = [...updates];
      updated[index].images = res.images;
      setUpdates(updated);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  const handleBulkSubmit = async () => {
    const validRows = updates.filter((row) => !row.error);
    if (validRows.length === 0) {
      toast.warning('No valid rows to submit');
      return;
    }

    try {
      await bulkUpdateStoreExpenses({ storeExpenses: validRows }).unwrap();
      toast.success(`✅ Successfully updated ${validRows.length} storeExpenses`);
      setResults(validRows.map((r) => ({
        index: r.index + 1,
        status: 'Success',
        message: 'Updated',
      })));
    } catch (err) {
      const failedResults = validRows.map((r) => ({
        index: r.index + 1,
        status: 'Failed',
        message: err?.data?.message || err.message,
      }));
      setResults(failedResults);
      toast.error('Bulk update failed');
    }
  };

  return (
    <div className="container mt-4">
      <h2>📦 Bulk MiniExpense Batch Update</h2>

      <Form.Group controlId="excelUpload" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx" onChange={handleFileUpload} />
      </Form.Group>

      {updates.length > 0 && (
        <>
          <h5>Preview & Validate: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>StoreExpense ID</th>
                <th>Date</th>
                <th>Account</th>
                <th>Account Name</th>
                <th>SubAccount</th>
                <th>Details</th>
                <th>Amount</th>
                <th>Category</th>
                <th>Notes</th>
                <th>Images</th>
                <th>Upload Image</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {updates.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td>{row.storeExpenseId}</td>
                  <td>{row.date}</td>
                  <td>{row.account}</td>
                  <td>{row.accountName}</td>
                  <td>{row.subAccount}</td>
                  <td>{row.details}</td>
                  <td>{row.amount}</td>
                  <td>{row.category}</td>
                  <td>{row.notes}</td>
                  <td>
                    {row.images.map((img, i) => (
                      <img key={i} src={img} alt="preview" style={{ width: '20px', height: '20px', marginRight: '5px' }} />
                    ))}
                  </td>
                  <td>
                    <Form.Control
                      type="file"
                      onChange={(e) => handleImageUpload(e, idx)}
                      disabled={!!row.error}
                    />
                    {loadingUpload && <Spinner animation="border" size="sm" />}
                  </td>
                  <td>
                    {row.error ? (
                      <span className="text-danger">{row.error}</span>
                    ) : (
                      <span className="text-success">✅</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button
            variant="primary"
            onClick={handleBulkSubmit}
            disabled={loadingUpdate}
            className="mt-3"
          >
            {loadingUpdate ? <Spinner animation="border" size="sm" /> : '🚀 Submit All Valid StoreExpenses'}
          </Button>
        </>
      )}

      {results.length > 0 && (
        <div className="mt-4">
          <h5>📋 Submission Results</h5>
          {results.map((r, i) => (
            <Alert key={i} variant={r.status === 'Success' ? 'success' : 'danger'}>
              Row {r.index}: {r.status} — {r.message}
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkStoreTransferBatchUpdateScreen;
